import { CircularProgress, TextField } from '@mui/material';
import React, { FormEvent, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { useSignOut } from '../../../../shared/hooks/oauthHook';
import {
  useAuthenticateTwoFactorLogin,
  useMaskedPhone,
  useRequestTwoFactorLogin,
} from '../../../../shared/hooks/twoFactorAuth';
import {
  Background,
  HospitalLogo,
  SectionContainer,
  SectionTitle,
} from '../../common/components/AccountComponent';
import { BlueRoundedButton } from '../../common/components/BlueRoundedButton';
import { CheckboxWithText } from '../../common/components/CheckboxWithText';
import { Spacer } from '../../common/components/Spacer';
import { REDIRECT_PATH_BEFORE_2FA } from '../../hooks/oauthHook';

const Content = styled.div`
  padding: 20px;
  margin-top: 20px;
  text-align: center;
  word-break: keep-all;
`;

const TextfieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Button = styled.button`
  width: 60px;
  height: 40px;
  background-color: #2e5be0;
  border: none;
  color: #ffffff;
  border-radius: 5px;
  cursor: pointer;
`;

const CheckboxText = styled.span`
  font-size: 14px;
  color: #666973;
`;

const TimerText = styled.div`
  margin-top: 10px;
  font-size: 14px;
  text-align: center;
  word-break: keep-all;
`;

export const TwoFactorAuthentication = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'features.2fa' });
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [remember30days, setRemeber30days] = useState(false);
  const [timerTime, setTimerTime] = useState(180);
  const [disabledResend, setDisabledResend] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const {
    data,
    isFetching: isFetchingMaskedPhone,
    error,
    isError,
  } = useMaskedPhone();
  useEffect(() => {
    if (isError && error.response?.status === 401) {
      console.error(error.message);
    }
  }, [error?.message, error?.response?.status, isError]);

  const { mutate: request2faLogin, isPending: isPendingRequestCode } =
    useRequestTwoFactorLogin({
      onSuccess() {
        setIsCodeSent(true);
      },
    });
  const onClickRequest2faButton = () => {
    request2faLogin();
  };
  const {
    mutate: authorize2fa,
    isPending: isPendingAuthorize2fa,
    error: authorizationError,
  } = useAuthenticateTwoFactorLogin({
    onSuccess() {
      setIsAuthenticated(true);
      const path = localStorage.getItem(REDIRECT_PATH_BEFORE_2FA);
      if (path) {
        location.replace(path);
      } else {
        location.replace('/');
      }
    },
  });
  const isCodeNotMatched =
    authorizationError?.response?.status === 400 &&
    authorizationError?.response?.data.code === '6000';

  useEffect(() => {
    if (isCodeSent && timerTime > 0) {
      const timerId = setInterval(() => {
        setTimerTime(prevTime => {
          const time = prevTime - 1;
          if (time === 0) {
            clearInterval(timerId);
          }
          return time;
        });
      }, 1000);

      if (timerTime < 160) {
        setDisabledResend(false);
      }
      return () => clearInterval(timerId);
    }
  }, [isCodeSent, timerTime]);

  const resetTimer = () => {
    request2faLogin();
    setIsCodeSent(true);
    setTimerTime(180);
    setDisabledResend(true);
  };

  const { mutate: signOut } = useSignOut();
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isPendingAuthorize2fa) {
        return;
      }
      e.preventDefault();
      signOut();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isPendingAuthorize2fa, signOut]);

  // FIXME: 로고를 클릭해서 랜딩으로 이동하면 signout 해야 함
  useEffect(() => {
    return () => {
      if (isCodeSent && isAuthenticated) {
        signOut();
      }
    };
  }, [isAuthenticated, isCodeSent, signOut]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    authorize2fa({ code, remember30days });
  };

  return (
    <Background>
      <SectionContainer as="form" onSubmit={handleSubmit}>
        <HospitalLogo />
        <SectionTitle>{t('title')}</SectionTitle>
        {isFetchingMaskedPhone ? (
          <>
            <Spacer height={40} />
            <CircularProgress size={60} />
          </>
        ) : (
          <>
            <Content>
              {isCodeSent
                ? t('authorize.content', { phone: data?.phoneNumberMasked })
                : t('sendCode.content', { phone: data?.phoneNumberMasked })}
            </Content>
            {isCodeSent && (
              <>
                <TextfieldWrapper>
                  <TextField
                    variant="standard"
                    autoComplete="off"
                    inputProps={{
                      maxLength: 6,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      style: { textAlign: 'center' },
                    }}
                    value={code}
                    onChange={e => setCode(e.target.value)}
                  />
                  <Button
                    type="button"
                    onClick={resetTimer}
                    disabled={disabledResend}>
                    {t('authorize.resend')}
                  </Button>
                </TextfieldWrapper>
                <CheckboxWithText
                  text={
                    <CheckboxText>{t('authorize.remember30days')}</CheckboxText>
                  }
                  $margin="18px 0 0 80px"
                  value={remember30days}
                  onChange={setRemeber30days}
                  $alignSelf="flex-start"
                />
                <TimerText>
                  {timerTime <= 0 ? (
                    <Trans
                      t={t}
                      i18nKey="authorize.timeExpired"
                      components={[<br />]}
                    />
                  ) : (
                    <>
                      {t('authorize.timeLeft')}{' '}
                      {String(Math.floor(timerTime / 60)).padStart(2, '0')}:
                      {String(timerTime % 60).padStart(2, '0')}
                    </>
                  )}
                </TimerText>
              </>
            )}

            <BlueRoundedButton
              marginTop="40px"
              type={isCodeSent ? undefined : 'button'}
              onClick={
                isPendingRequestCode || isCodeSent
                  ? undefined
                  : onClickRequest2faButton
              }
              disabled={timerTime <= 0}>
              {isCodeSent ? (
                isPendingAuthorize2fa ? (
                  <CircularProgress size={24} style={{ color: 'white' }} />
                ) : (
                  t('authorize.submit')
                )
              ) : isPendingRequestCode ? (
                <CircularProgress size={24} style={{ color: 'white' }} />
              ) : (
                t('sendCode.button')
              )}
            </BlueRoundedButton>
            <Spacer height={16} />
            {isCodeNotMatched && <span>인증 번호가 일치하지 않습니다.</span>}
          </>
        )}
      </SectionContainer>
    </Background>
  );
};
